import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { convertToRaw, convertFromRaw } from "draft-js";
import { convertFromHTML, convertToHTML  } from "draft-convert";
import { makeStyles } from "@material-ui/core/styles";
import MUIRichTextEditor from "mui-rte";
import { useStyles } from "./styles";
import FormatAlignCenterIcon from "@material-ui/icons/FormatAlignCenter";
import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";
import FormatAlignRightIcon from "@material-ui/icons/FormatAlignRight";

import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import clsx from "clsx";

const defaultStyle = {
  fontSize: 1,
  currentText: "",
  alignment: "left",
};

const fontRegex = /font-size:\s*([0-9]+(?:\.[0-9]+)?)em/i;
const alignmentRegex = /text-align:\s*(left|center|right)/i;
const nonDigitRegex = /[^0-9.]/g;
const alignmentValueRegex = /(left|center|right)/i;

const useEditorStyles = (props) =>
  makeStyles((theme) => ({
    fontStyle: {
      color: theme.palette.white?.main,
      fontSize: `${props.fontSize}em !important`,
      display: "block",
      textAlign: `${props.alignment}`,
    },
  }));

const RichTextEditor = ({ content, disabled, editorLabel, onChange, customEditorStyles }) => {
  const classes = useStyles();
  const [richTextContent, setRichTextContent] = useState();
  const [customStyle, setCustomStyle] = useState(defaultStyle);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const editorStyles = useEditorStyles(customStyle)();

  useEffect(() => {
    if (content && content !== "") {
      let fontSize = defaultStyle.fontSize;
      let alignment = defaultStyle.alignment;

      const fontMatch = content.match(fontRegex);
      if (fontMatch && fontMatch[1]) {
        const parsed = parseFloat(fontMatch[1]);
        if (!Number.isNaN(parsed)) fontSize = parsed;
      }

      const alignMatch = content.match(alignmentRegex);
      if (alignMatch && alignMatch[1]) {
        alignment = alignMatch[1];
      }

      setCustomStyle((prev) => ({ ...prev, alignment, fontSize }));
    } else {
      // Reset to defaults when content is empty or undefined
      setCustomStyle(defaultStyle);
    }

    const safeContent = typeof content === "string" ? content : "";
    const stripLineFeeds = safeContent.replace(new RegExp('\n', 'g'), "");
    const contentHTML = convertFromHTML(`<div>${stripLineFeeds}</div>`);
    const rawState = convertToRaw(contentHTML);
    const updatedContent = JSON.stringify(rawState);
    setRichTextContent(updatedContent);
  }, [content]);

  function handleConvert(styles) {
    const textContent = styles.currentText;
    const textAlignValue = styles.alignment ?? defaultStyle.alignment;
    const fontSizeNum = typeof styles.fontSize === "number" && !Number.isNaN(styles.fontSize)
      ? styles.fontSize
      : defaultStyle.fontSize;
    return `<div style="text-align:${textAlignValue};font-size:${fontSizeNum}em;">${textContent}</div>`;
  }

  const increaseFontSize = () => {
    setCustomStyle((prevStyle) => {
      const current = (typeof prevStyle.fontSize === "number" && !Number.isNaN(prevStyle.fontSize))
        ? prevStyle.fontSize
        : defaultStyle.fontSize;
      const newStyle = { ...prevStyle, fontSize: current + 0.5 };
      handleRateDisplayChange(newStyle);
      return newStyle;
    });
  };

  const decreaseFontSize = () => {
    setCustomStyle((prevStyle) => {
      const current = (typeof prevStyle.fontSize === "number" && !Number.isNaN(prevStyle.fontSize))
        ? prevStyle.fontSize
        : defaultStyle.fontSize;
      const newStyle = { ...prevStyle, fontSize: Math.max(0.5, current - 0.5) };
      handleRateDisplayChange(newStyle);
      return newStyle;
    });
  };

  const handleInputChange = (data) => {
    const textContent = convertToHTML(data.getCurrentContent());
    const contentState = convertFromRaw(convertToRaw(convertFromHTML("<div>" + textContent + "</div>")));
    const htmlText = convertToHTML(contentState);
    setCustomStyle((prevStyle) => {
      const newStyle = { ...prevStyle, currentText: htmlText };
      triggerChangeHandler(newStyle, hasBeenFocused);
      return newStyle;
    });
  };

  const changeAlignment = (align) => {
    setCustomStyle((prevStyle) => {
      const newStyle = { ...prevStyle, alignment: align };
      handleRateDisplayChange(newStyle);
      return newStyle;
    });
  };

  const triggerChangeHandler = (updatedStyle, focusState) => {
    const messageString = handleConvert(updatedStyle);
    onChange(messageString, focusState);
  };

  const handleFocus = () => {
    setHasBeenFocused(true);
  };

  const handleRateDisplayChange = (display) => {
    setHasBeenFocused(() => {
      triggerChangeHandler(display, true);
      return true;
    })
  }

  return (
      <MUIRichTextEditor
        readOnly={disabled}
        classes={{
          editorContainer: clsx("richTextEditor",
            customEditorStyles.editorContainer? customEditorStyles.editorContainer: classes.editorContainer,
            editorStyles.fontStyle
          ),
          toolbar: customEditorStyles.toolbar? customEditorStyles.toolbar: classes.toolbar,
          container: customEditorStyles.container? customEditorStyles.container: classes.container,
        }}
        defaultValue={richTextContent}
        label={editorLabel}
        controls={[
          "leftAlign",
          "centerAlign",
          "rightAlign",
          "increaseText",
          "decreaseText",
        ]}
        onChange={handleInputChange}
        onFocus={handleFocus}
        customControls={[
          {
            name: "leftAlign",
            icon: <FormatAlignLeftIcon className={clsx("leftAlignBtn", classes.icons)} />,
            type: "callback",
            onClick: () => {
              changeAlignment("left");
            },
          },
          {
            name: "centerAlign",
            icon: <FormatAlignCenterIcon className={clsx("centerAlignBtn", classes.icons)} />,
            type: "callback",
            onClick: () => {
              changeAlignment("center");
            },
          },
          {
            name: "rightAlign",
            icon: <FormatAlignRightIcon className={clsx("rightAlignBtn", classes.icons)} />,
            type: "callback",
            onClick: () => {
              changeAlignment("right");
            },
          },

          {
            name: "increaseText",
            icon: <AddIcon className={clsx("increaceTextBtn", classes.icons)} />,
            type: "callback",
            onClick: () => {
              increaseFontSize();
            },
          },
          {
            name: "decreaseText",
            icon: <RemoveIcon className={clsx("decreaseTextBtn", classes.icons)} />,
            type: "callback",
            onClick: () => {
              decreaseFontSize();
            },
          },
        ]}
      />
  );
};

RichTextEditor.defaultProps = {
  onChange: () => {},
  content: "",
  disabled: false,
  editorLabel: "Rich Text Editor",
  customEditorStyles: {}
};

RichTextEditor.propTypes = {
  content: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  editorLabel: PropTypes.string,
  customEditorStyles: PropTypes.shape({
    container: PropTypes.object,
    toolbar: PropTypes.object,
    editorContainer: PropTypes.object,
  }),
};

export default RichTextEditor;

